<template>
  <div class="py-4 space-y-6">
    <!-- Title -->
    <div class="text-center mb-6">
      <h1 class="text-xl font-bold text-foreground mb-2">
        {{ $t('report.clients_overview_info.title') }}
      </h1>
      <p class="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
        {{ $t('report.clients_overview_info.description') }}
      </p>
    </div>

    <!-- Indicators -->
    <div class="space-y-4">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_overview_info.how_to_read') }}
        </h2>
      </div>

      <!-- Mini illustration matching the actual card layout -->
      <div class="rounded-lg p-3 info-example-bg">
        <div class="flex items-baseline justify-between mb-2">
          <div>
            <p class="text-[8px] font-semibold text-muted-foreground/60 uppercase tracking-wider mb-0.5">{{ $t('report.clients_label') }}</p>
            <span class="text-[24px] font-extrabold text-foreground leading-none">124</span>
          </div>
          <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold" style="background: rgba(22,163,74,0.10); color: #16A34A">+8.2 %</span>
        </div>
        <div class="flex w-full h-1 rounded-full overflow-hidden mb-1.5">
          <span class="h-full" style="background-color: #10B981; width: 38%"></span>
          <span class="h-full" style="background-color: #6366F1; width: 62%"></span>
        </div>
        <div class="grid grid-cols-2 gap-1.5">
          <div class="rounded-md px-2 py-1" style="background: rgba(0,0,0,0.02)">
            <div class="flex items-center gap-1 mb-0.5">
              <span class="w-1 h-1 rounded-full" style="background: #10B981"></span>
              <span class="text-[7px] font-semibold text-muted-foreground/60 uppercase">{{ $t('report.clients_overview_info.new_label') }}</span>
            </div>
            <span class="text-[13px] font-bold text-foreground">47</span>
            <span class="text-[8px] font-bold ml-1" style="color: #16A34A">+12.0 %</span>
          </div>
          <div class="rounded-md px-2 py-1" style="background: rgba(0,0,0,0.02)">
            <div class="flex items-center gap-1 mb-0.5">
              <span class="w-1 h-1 rounded-full" style="background: #6366F1"></span>
              <span class="text-[7px] font-semibold text-muted-foreground/60 uppercase">{{ $t('report.clients_overview_info.returning_label') }}</span>
            </div>
            <span class="text-[13px] font-bold text-foreground">77</span>
            <span class="text-[8px] font-bold ml-1" style="color: #16A34A">+5.5 %</span>
          </div>
        </div>
      </div>

      <!-- Indicator descriptions -->
      <div class="space-y-3">
        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #06C8FF"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_overview_info.total_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_overview_info.total_desc') }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #10B981"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_overview_info.new_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_overview_info.new_desc') }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #6366F1"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_overview_info.returning_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_overview_info.returning_desc') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Secondary metrics -->
    <div class="space-y-2.5">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_overview_info.metrics_section') }}
        </h2>
      </div>

      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_overview_info.kpi_revenue_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_overview_info.kpi_revenue_desc') }}
        </p>
      </div>

      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_overview_info.kpi_basket_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_overview_info.kpi_basket_desc') }}
        </p>
      </div>

      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_overview_info.kpi_purchases_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_overview_info.kpi_purchases_desc') }}
        </p>
      </div>

      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_overview_info.retention_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_overview_info.retention_desc') }}
        </p>
      </div>
    </div>

    <!-- Equitable evolution explanation -->
    <div class="space-y-2">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_overview_info.evolution_section') }}
        </h2>
      </div>
      <p class="text-xs text-muted-foreground leading-relaxed ml-3">
        {{ $t('report.clients_overview_info.evolution_desc') }}
      </p>
    </div>

    <!-- Scope note -->
    <div class="info-note rounded-lg px-3 py-2.5">
      <p class="text-xs text-muted-foreground leading-relaxed">
        <span class="font-semibold text-foreground">{{ $t('report.clients_overview_info.anonymous_note_label') }}</span>
        {{ $t('report.clients_overview_info.anonymous_note') }}
      </p>
    </div>

  </div>
</template>

<script setup lang="ts">
// Content-only component — no props needed.
// Injected into a BottomSheet from CenterClientsReport.
</script>

<style scoped>
.info-note {
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.15);
}
:is(.dark *).info-note {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.12);
}
.info-example-bg {
  background: rgba(107, 114, 128, 0.06);
  border: 1px solid rgba(107, 114, 128, 0.1);
}
:is(.dark *).info-example-bg {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.info-metric-item {
  padding-left: 0.75rem;
  border-left: 2px solid rgba(107, 114, 128, 0.15);
}
:is(.dark *).info-metric-item {
  border-left-color: rgba(255, 255, 255, 0.1);
}
.info-metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--foreground);
}
</style>
